import React, { useState } from "react";
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";

interface SimpleBulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
}

const SimpleBulkImportModal: React.FC<SimpleBulkImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [pastedData, setPastedData] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!pastedData.trim()) {
      setError("Please paste product data");
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      // Parse tab-separated data
      const lines = pastedData.split("\n").filter((line) => line.trim());
      
      // Skip header row if present
      const dataLines = lines.filter((line, index) => {
        if (index === 0) {
          const lower = line.toLowerCase();
          return !(lower.includes("proizvod") || lower.includes("cena") || lower.includes("product"));
        }
        return true;
      });

      const products = dataLines.map((line) => {
        const columns = line.split("\t");
        return {
          name: columns[0]?.trim() || "",
          price: columns[1]?.trim() || "0",
          link: columns[2]?.trim() || "",
          image: columns[3]?.trim() || "",
        };
      });

      console.log(`📦 [IMPORT] Preparing to import ${products.length} products:`, products);

      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/products/bulk-import`, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ products }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to import products");
      }

      const data = await response.json();
      console.log(`✅ [IMPORT] Response received:`, data);
      
      if (data.success) {
        setResult(data.data);
        console.log(`✅ [IMPORT] Successfully imported - Created: ${data.data.created}, Updated: ${data.data.updated}`);
        onImport();
      } else {
        throw new Error(data.message || "Failed to import products");
      }
    } catch (err) {
      console.error("Error importing products:", err);
      setError(err instanceof Error ? err.message : "Failed to import products");
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Bulk Import Products
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste Product Data (Tab-separated)
            </label>
            <textarea
              value={pastedData}
              onChange={(e) => setPastedData(e.target.value)}
              placeholder="Paste tab-separated product data here..."
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {pastedData.split("\n").filter((l) => l.trim()).length} rows
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-900 mb-2">
                    Import Results
                  </h3>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>Created: {result.created}</p>
                    <p>Updated: {result.updated}</p>
                    <p>Skipped: {result.skipped}</p>
                    {result.errors && result.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">Errors:</p>
                        <ul className="list-disc list-inside text-xs">
                          {result.errors.slice(0, 5).map((err: any, idx: number) => (
                            <li key={idx}>
                              Row {err.row}: {err.error}
                            </li>
                          ))}
                          {result.errors.length > 5 && (
                            <li>... and {result.errors.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={!pastedData.trim() || importing}
              className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Products
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleBulkImportModal;
